import { clamp } from '#src/lib/math.ts';

export type Point = readonly [x: number, y: number];

type Segment =
  | { line: Point }
  | { curve: readonly [control1: Point, control2: Point, end: Point] };

const START: Point = [1.75354, 16];

const SEGMENTS: Segment[] = [
  { line: [1.75354, 10.7979] },
  {
    curve: [
      [1.75359, 9.0851],
      [2.6924, 7.4913],
      [4.23303, 6.625],
    ],
  },
  { line: [13.4586, 1.4375] },
  {
    curve: [
      [15.0015, 0.56991],
      [16.906, 0.56991],
      [18.4489, 1.4375],
    ],
  },
  { line: [27.6744, 6.625] },
  {
    curve: [
      [29.2151, 7.4913],
      [30.1539, 9.0851],
      [30.1539, 10.7979],
    ],
  },
  { line: [30.1539, 16] },
];

const formatPoint = ([x, y]: Point) => `${x} ${y}`;

export const TOP_BORDER_PATH = [
  `M${formatPoint(START)}`,
  ...SEGMENTS.map(segment =>
    'line' in segment
      ? `L${formatPoint(segment.line)}`
      : `C${segment.curve.map(formatPoint).join(' ')}`
  ),
].join(' ');

const CURVE_SAMPLES = 16;

const cubicPoint = (
  [x0, y0]: Point,
  [x1, y1]: Point,
  [x2, y2]: Point,
  [x3, y3]: Point,
  t: number
): Point => {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return [a * x0 + b * x1 + c * x2 + d * x3, a * y0 + b * y1 + c * y2 + d * y3];
};

const flatten = () => {
  const points: Point[] = [START];
  const segmentEndIndices: number[] = [];
  SEGMENTS.forEach(segment => {
    const from = points[points.length - 1];
    if ('line' in segment) {
      points.push(segment.line);
    } else {
      const [control1, control2, end] = segment.curve;
      for (let step = 1; step <= CURVE_SAMPLES; step += 1) {
        points.push(
          cubicPoint(from, control1, control2, end, step / CURVE_SAMPLES)
        );
      }
    }
    segmentEndIndices.push(points.length - 1);
  });
  return { points, segmentEndIndices };
};

const { points: POLYLINE, segmentEndIndices: SEGMENT_END_INDICES } = flatten();

const CUMULATIVE_LENGTHS = POLYLINE.reduce<number[]>(
  (lengths, [x, y], index) => {
    if (index === 0) {
      return [0];
    }
    const [previousX, previousY] = POLYLINE[index - 1];
    return [
      ...lengths,
      lengths[index - 1] + Math.hypot(x - previousX, y - previousY),
    ];
  },
  []
);

const pointAtLength = (target: number): Point => {
  const endIndex = CUMULATIVE_LENGTHS.findIndex(length => length >= target);
  if (endIndex === 0) {
    return START;
  }
  if (endIndex === -1) {
    return POLYLINE[POLYLINE.length - 1];
  }

  const [startX, startY] = POLYLINE[endIndex - 1];
  const [endX, endY] = POLYLINE[endIndex];
  const startLength = CUMULATIVE_LENGTHS[endIndex - 1];
  const t =
    (target - startLength) / (CUMULATIVE_LENGTHS[endIndex] - startLength);
  return [startX + (endX - startX) * t, startY + (endY - startY) * t];
};

const LEFT_CORNER_SEGMENT = 1;
const RIGHT_SLANTED_SIDE_SEGMENT = 4;

const TOP_SIDES_START =
  CUMULATIVE_LENGTHS[SEGMENT_END_INDICES[LEFT_CORNER_SEGMENT]];
const TOP_SIDES_END =
  CUMULATIVE_LENGTHS[SEGMENT_END_INDICES[RIGHT_SLANTED_SIDE_SEGMENT]];

const lengthAlongTopSides = (fraction: number) =>
  TOP_SIDES_START + clamp(fraction, 0, 1) * (TOP_SIDES_END - TOP_SIDES_START);

export const pointAlongTopSides = (fraction: number): Point =>
  pointAtLength(lengthAlongTopSides(fraction));

const cross = ([ax, ay]: Point, [bx, by]: Point) => ax * by - ay * bx;

/**
 * Distance from `origin` to the top border along a ray `angle` degrees
 * clockwise from straight up.
 */
export const distanceToTopBorder = (origin: Point, angle: number) => {
  const radians = (angle * Math.PI) / 180;
  const direction: Point = [Math.sin(radians), -Math.cos(radians)];
  const [originX, originY] = origin;

  return POLYLINE.slice(1).reduce((closest, [endX, endY], index) => {
    const [startX, startY] = POLYLINE[index];
    const side: Point = [endX - startX, endY - startY];
    const denominator = cross(direction, side);
    if (denominator === 0) {
      return closest;
    }
    const toStart: Point = [startX - originX, startY - originY];
    const along = cross(toStart, side) / denominator;
    const onSide = cross(toStart, direction) / denominator;
    return along >= 0 && onSide >= 0 && onSide <= 1
      ? Math.min(closest, along)
      : closest;
  }, Infinity);
};

const NEIGHBOUR_SPLIT_BOTTOM: Point = [16, 16];
const NEIGHBOUR_REGION_BOTTOM = 18;

const polygon = (points: Point[]) => points.map(formatPoint).join(' ');

export const PREVIOUS_TIER_REGION = polygon([
  [-12, 0],
  NEIGHBOUR_SPLIT_BOTTOM,
  [16, NEIGHBOUR_REGION_BOTTOM],
  [-12, NEIGHBOUR_REGION_BOTTOM],
]);

export const NEXT_TIER_REGION = polygon([
  [44, 0],
  NEIGHBOUR_SPLIT_BOTTOM,
  [16, NEIGHBOUR_REGION_BOTTOM],
  [44, NEIGHBOUR_REGION_BOTTOM],
]);
